import { Box, Text, Tooltip } from '@betterment/samba-react';
import { formatNumberWithDecimals } from '@betterment/shared-components';

import { PercentText } from '../../../../../Shared/components-v2/percent-text/PercentText';
import { PortfolioStrategyCardQuery } from '../../graphql/portfolioStrategyCard.generated';

type Portfolio = NonNullable<
  Extract<PortfolioStrategyCardQuery['envelopeAccount'], { __typename: 'ManagedAccount' }>['portfolio']
>;

type Props = { portfolio: Portfolio; hasEverBeenFunded?: boolean };

export const PortfolioStrategyStats: React.FC<Props> = ({ portfolio, hasEverBeenFunded = false }) => {
  const {
    drift,
    expenseRatio,
    rebalance: { explanation: rebalancingTooltip, isEnabled: isRebalanceEnabled },
  } = portfolio;

  const riskLevel = 'riskLevel' in portfolio ? portfolio.riskLevel : undefined;

  const dividendYield = (() => {
    switch (portfolio.__typename) {
      case 'SelfDirectedPortfolio':
        return { value: portfolio.dividendYield, explanation: portfolio.dividendYieldExplanation };
      case 'RiskLevelBasedPortfolio':
        return { value: portfolio.riskLevel.dividendYield, explanation: portfolio.riskLevel.dividendYieldExplanation };
      default:
        return portfolio;
    }
  })();

  const bondYieldItem = riskLevel?.bondYields && {
    label: 'Yield',
    tooltip: riskLevel.bondYields.blendedSecYieldExplanation,
    value: riskLevel.bondYields.blendedSecYield,
  };
  const afterTaxYieldItem = riskLevel?.bondYields && {
    label: 'Your after-tax yield',
    tooltip: riskLevel.bondYields.afterTaxYieldExplanation,
    value: riskLevel.bondYields.afterTaxYield,
  };
  const driftItem = drift &&
    hasEverBeenFunded && {
      label: 'Drift',
      tooltip: drift.explanation,
      value: `${drift.isAboveThreshold ? 'High ' : 'Normal '}(${formatNumberWithDecimals(drift.percent)}%)`,
    };
  const rebalancing = hasEverBeenFunded && {
    label: 'Rebalancing',
    tooltip: rebalancingTooltip,
    value: isRebalanceEnabled ? 'On' : 'Off',
  };
  const dividendYieldItem = {
    label: 'Dividend yield',
    tooltip: dividendYield.explanation,
    value: dividendYield.value,
  };
  const expenseRatioItem = {
    label: 'Expense ratio',
    tooltip: expenseRatio.explanation,
    value: expenseRatio.value,
  };

  const portfolioStrategyStats = [
    expenseRatioItem,
    dividendYieldItem,
    bondYieldItem,
    afterTaxYieldItem,
    rebalancing,
    driftItem,
  ];

  return (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'space.300',
      }}
    >
      {portfolioStrategyStats
        .filter(item => !!item)
        .map(stat => {
          return (
            <Box key={`portfolio-strategy-stats-${stat.label}`} css={{ display: 'grid', gap: 'space.50' }}>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.50' }}>
                <Text variant="text50">{stat.label}</Text>
                <Tooltip>
                  <Tooltip.Trigger asChild={true}>
                    <Tooltip.IconButton />
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    <Box dangerouslySetInnerHTML={{ __html: stat.tooltip }} />
                  </Tooltip.Content>
                </Tooltip>
              </Box>
              {typeof stat.value === 'number' ? (
                <PercentText percent={stat.value} precision={2} />
              ) : (
                <Text>{stat.value}</Text>
              )}
            </Box>
          );
        })}
    </Box>
  );
};
